import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import { Layer } from './layer';
import { bbox as bboxStrategy } from 'ol/loadingstrategy.js';
import { Extent } from 'ol/extent';
import { StyleFunction } from 'ol/style/Style';
import { Geometry } from 'geojson';
import { Feature } from 'ol';

export class WfsLayer extends Layer {

    declare id: string;

    private _styleFunction: StyleFunction | undefined;
    private _vectorLayer: VectorLayer | undefined;
    private _vectorSource: VectorSource | undefined;
    private _cqlFilters: Object[] = [];

    constructor(
        id: string,
        label: string,
        type: string,
        public typeNames: string,
        public outputFormat: string = 'application/json',
        public projectionCode: string,
        public minZoom: number,
        public maxZoom: number,
        public hasLegend: boolean = false,
        public url: string,
        public opacity: number
    ) {
        super(id, label, type);
    }

    setStyleFunction(styleFunction: StyleFunction): void {
        this._styleFunction = styleFunction;
    }

    cqlFilterExist(filterId: string): boolean {
        return this._cqlFilters.some(f => f["id"] === filterId);
    }

    replaceFilterById(newItem: any) {
        const idx = this._cqlFilters.findIndex(c => c["id"] === newItem["id"]);
        if (idx === -1) return this._cqlFilters;
        const copy = this._cqlFilters.slice();
        copy[idx] = newItem;
        return copy;
    }

    addCqlFilter(cqlFilter: object): void {
        if (this.cqlFilterExist(cqlFilter["id"])) {
            this._cqlFilters = this.replaceFilterById(cqlFilter);
        } else {
            this._cqlFilters.push(cqlFilter);
        }

        if (this._vectorSource) {
            this._vectorSource.clear();
            this._vectorSource.refresh();
        }
    }

    isCqlFilterEnabled(filterId: string): boolean {
        return this._cqlFilters.some(f => f["id"] == filterId);
    }

    removeCqlFilter(filterId: string) {
        this._cqlFilters = this._cqlFilters.filter(f => f["id"] !== filterId);
        this._vectorSource.clear();
        this._vectorSource.refresh();
    }

    removeCqlFilters() {
        this._cqlFilters = []
        this._vectorSource.clear();
        this._vectorSource.refresh();
    }

    buildCqlFilter(extent: Extent): string {
        const geomField = "geometry";

        const filters = [
            `BBOX(${geomField}, ${extent.join(',')}, '${this.projectionCode}')`,
            ...this._cqlFilters.map(f => f.toString())
        ]

        return filters.join(" AND ");
    }
    
    async queryFeatures(cqlFilter: string): Promise<Feature[]> {
        const url =
            this.url +
            '?SERVICE=WFS' +
            '&REQUEST=GetFeature' +
            '&VERSION=2.0.0' +
            '&TYPENAMES=' + encodeURIComponent(this.typeNames) +
            '&outputFormat=' + encodeURIComponent(this.outputFormat) +
            '&SRSNAME=' + encodeURIComponent(this.projectionCode) +
            '&CQL_FILTER=' + encodeURIComponent(cqlFilter);
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`WFS request failed: ${response.status}`);
        }
        const json = await response.json();
        const format = new GeoJSON();
        return format.readFeatures(json, {
            featureProjection: this.projectionCode
        });
    }


    toOlLayer() {
        let self = this;
        const zoomThreshold = 14;

        // configure vector source
        const vectorSource = new VectorSource({
            format: new GeoJSON(),
            url: function (extent: Extent) {
                let url =
                    self.url +
                    '?SERVICE=WFS&REQUEST=GetFeature&VERSION=2.0.0' +
                    '&TYPENAMES=' + self.typeNames +
                    '&outputFormat=' + self.outputFormat +
                    '&SRSNAME=' + self.projectionCode;

                if (self._cqlFilters.length > 0) {
                    const cql = self.buildCqlFilter(extent);
                    url += '&CQL_FILTER=' + encodeURIComponent(cql);

                } else {
                    url += '&BBOX=' + extent.join(',') + ',' + self.projectionCode;
                }

                return url;

            },
            strategy: bboxStrategy,
        });

        self._vectorSource = vectorSource;
        // configure vector layer
        const vectorLayer = new VectorLayer({
            source: vectorSource,
            style: this._styleFunction != undefined ? this._styleFunction : undefined,
            minZoom: self.minZoom,
            maxZoom: self.maxZoom,
            opacity: self.opacity ?? 1
        });

        self._vectorLayer = vectorLayer;
        vectorLayer.set("customId", this.id);
        return vectorLayer;
    }

    // needed for events access via components
    getOlLayer(): VectorLayer {
        return this._vectorLayer;
    }

    getOlLayerSource(): VectorSource {
        return this._vectorSource;
    }

    cloneFilters(otherLayerWithFilters: WfsLayer) {
        this._cqlFilters = otherLayerWithFilters._cqlFilters;

        this._cqlFilters.forEach(filter => {
            this.addCqlFilter(filter);
        });
    }

}